import { AllocateTickets } from "@/components/dashboard/accreditation/AllocateTickets";
import { MoveIndividualOrGroup } from "@/components/dashboard/accreditation/bulk-actions/MoveIndividualOrGroup";
import { CreateGroupDialog } from "@/components/dashboard/accreditation/create/CreateGroupDialog";
import { CreateIndividualDialog } from "@/components/dashboard/accreditation/create/CreateIndividualDialog";
import { SendEmail } from "@/components/dashboard/accreditation/email/SendEmail";
import { GlobalSearch } from "@/components/dashboard/accreditation/GlobalSearch";
import { GroupDetails } from "@/components/dashboard/accreditation/GroupDetails";
import { GroupList } from "@/components/dashboard/accreditation/GroupList";
import { GroupOptionsButton } from "@/components/dashboard/accreditation/GroupOptionsDropdown";
import { IndividualList } from "@/components/dashboard/accreditation/individual-list/IndividualList";
import { RequestPayment } from "@/components/dashboard/accreditation/payments/RequestPayment";
import { GroupSettings } from "@/components/dashboard/accreditation/settings/GroupSettings";
import { IndividualsSheet } from "@/components/dashboard/accreditation/sheet/IndividualsSheet";
import { TicketBadge } from "@/components/dashboard/accreditation/TicketBadge";
import { ViewGroupEmails } from "@/components/dashboard/accreditation/email/ViewGroupEmails";
import {
  BulkAction,
  BulkActionsButton,
} from "@/components/dashboard/BulkActionsButton";
import { StatusBadge } from "@/components/dashboard/flows/StatusBadge";
import { DashboardLayout } from "@/components/dashboard/layouts/DashboardLayout";
import { TabNav } from "@/components/dashboard/nav/TabNav";
import { Documents } from "@/components/portal/accreditation/Documents";
import { Loading } from "@/components/portal/Loading";
import { TicketIssueStatus } from "@/constants/accreditation";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import {
  AccreditationGroup,
  AccreditationIndividual,
  ManageAccreditationGroupRequest,
  TicketAllocation,
} from "@/models/accreditation";
import { PaginatedResponse } from "@/models/Dashboard";
import { Flow } from "@/models/flows";
import {
  individualsValidationSchema,
  individualsValidationSchemaWithUuid,
} from "@/pages/accreditation/manage-group";
import { useAccreditationStore } from "@/store/accreditation";
import { useFlowStore } from "@/store/flow";
import { invalidateAccreditationGroups } from "@/utils/accreditation";
import { formatDateWithTime } from "@/utils/date";
import { classNames } from "@/utils/styles";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  PencilSquareIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  BoltIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Column } from "@silevis/reactgrid";
import axios, { AxiosResponse } from "axios";
import clsx from "clsx";
import { set } from "date-fns";
import { Formik, FormikValues } from "formik";
import {
  Bolt,
  BoxesIcon,
  CheckCircleIcon,
  CornerUpRight,
  DollarSignIcon,
  DownloadIcon,
  LinkIcon,
  ListIcon,
  Mail,
  MailIcon,
  PlusIcon,
  SaveIcon,
  Search,
  Send,
  SendIcon,
  Shapes,
  SheetIcon,
  Shield,
  SquareArrowOutUpRightIcon,
  TableOfContents,
  Ticket,
  TicketIcon,
  TicketsIcon,
  UserIcon,
  UserPlus,
  UserPlus2,
} from "lucide-react";
import { send } from "process";
import { Fragment, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Sheet } from "@/components/dashboard/sheet/Sheet";
import { v4 as uuidv4 } from "uuid";
import { ButtonSpinner } from "@/components/ButtonSpinner";

export const fetchGroup = async (
  groupId: number,
): Promise<AccreditationGroup> => {
  const url = `${BASE_URL}/accreditation/groups/${groupId}`;
  const { data } = await axios.get(url);
  return data;
};

const fetchGroups = async (
  page: number,
  filters?: Record<string, any>,
): Promise<PaginatedResponse<AccreditationGroup>> => {
  const url = `${BASE_URL}/accreditation/groups?page=${page}&${new URLSearchParams(
    filters,
  ).toString()}`;
  const { data } = await axios.get(url);
  return data;
};

export const fetchIndividuals = async (
  page: number,
  filters?: Record<string, any>,
): Promise<PaginatedResponse<AccreditationIndividual>> => {
  const url = `${BASE_URL}/accreditation/individuals?page=${page}&${new URLSearchParams(
    filters,
  ).toString()}&pageSize=1000`;
  const { data } = await axios.get(url);
  return data;
};

export const resendInvitation = async (individualId: number) => {
  try {
    await axios.post(
      `${BASE_URL}/accreditation/individuals/${individualId}/resend-invite`,
    );
    toast.success("📨 Invitation resent successfully");
    console.log("Invitation resent successfully");
  } catch (error) {
    console.error("Error resending invitation:", error);
  }
};

export const AccreditationGroups = () => {
  const {
    createGroupOpen,
    setCreateGroupOpen,
    createIndividualOpen,
    setCreateIndividualOpen,
    createGroupFromTemplateOpen,
    setCreateGroupFromTemplateOpen,
    setAllocateTicketsOpen,
    setIndividual,
    bulkActionsLoading,
    setBulkActionsLoading,
    groupToEdit,
    setGroupToEdit,
    searchOpen,
    setSearchOpen,
    selectedIndividualViewMode,
  } = useAccreditationStore();
  const isDarkMode = useDarkMode();

  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [groupContactDialogOpen, setGroupContactDialogOpen] = useState(false);

  // Deleting a Group
  const [groupToDeleteDialogOpen, setGroupToDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<AccreditationGroup | null>(
    null,
  );

  // Deleting an Individual
  const [individualToDeleteDialogOpen, setIndividualToDeleteDialogOpen] =
    useState(false);
  const [individualToDelete, setIndividualToDelete] =
    useState<AccreditationIndividual | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Group Search
  const [groupSearch, setGroupSearch] = useState("");
  const [groupPage, setGroupPage] = useState(
    parseInt(searchParams.get("groupsPage") || "1"),
  );

  // Individual Search
  const [individualSearch, setIndividualSearch] = useState("");
  const [individualPage, setIndividualPage] = useState(
    parseInt(searchParams.get("individualsPage") || "1"),
  );

  // Deleting a Group Contact
  const [groupContactToDeleteDialogOpen, setGroupContactToDeleteDialogOpen] =
    useState(false);
  const [groupContactToModify, setGroupContactToModify] =
    useState<AccreditationIndividual | null>(null);

  const [viewAll, setViewAll] = useState<boolean>(false);

  const tabParam = searchParams.get("tab");
  const [selectedTab, setSelectedTab] = useState(
    tabParam ? tabParam : "Overview",
  );

  // Update query param when tab changes
  useEffect(() => {
    if (selectedTab !== tabParam) {
      setSearchParams({ tab: selectedTab });
    }
    // eslint-disable-next-line
  }, [selectedTab]);

  // Update state if query param changes (e.g. via browser navigation)
  useEffect(() => {
    if (tabParam && tabParam !== selectedTab) {
      setSelectedTab(tabParam);
    }
    // eslint-disable-next-line
  }, [tabParam]);

  // Fetching group details
  const { data, error, isLoading, isError } = useQuery<AccreditationGroup>(
    ["group", groupId],
    () => fetchGroup(parseInt(groupId)),
    {
      // keepPreviousData: true,
      enabled: !!groupId,
    },
  );

  // Posting data
  const handleSubmit = async (values: FormikValues) => {
    try {
      if (groupId) {
        values["parent"] = groupId;
      }
      const response = await axios.post(
        `${BASE_URL}/accreditation/groups`,
        values,
      );
      console.log("Group created successfully:", response.data);
      setCreateGroupOpen(false);
      // Invalidate and refetch the groups query
      queryClient.invalidateQueries(["groups"]);
    } catch (error) {
      console.error("Error creating group:", error);
    }
  };

  // Deleting data
  const handleDelete = async (groupIdToDelete: number) => {
    try {
      await axios.delete(`${BASE_URL}/accreditation/groups/${groupIdToDelete}`);
      console.log("Group contact deleted successfully");
      navigate(
        `/dashboard/accreditation/groups${groupId ? `/${groupId}` : ""}`,
      );
      // Invalidate and refetch the groups query
      invalidateAccreditationGroups(
        queryClient,
        groupId ? parseInt(groupId) : 0,
      );
    } catch (error) {
      console.error("Error deleting group:", error);
    }
  };

  // Deleting individual
  const handleIndividualDelete = async (
    individual: AccreditationIndividual,
  ) => {
    try {
      await axios.delete(
        `${BASE_URL}/accreditation/individuals/${individual.id}`,
      );
      console.log("Individual deleted successfully");
      queryClient.invalidateQueries(["individuals", groupId?.toString()]);
      invalidateAccreditationGroups(queryClient, groupId);
    } catch (error) {
      console.error("Error deleting group:", error);
    }
  };

  // Editing data
  const handleEditOpen = async (groupIdToEdit: number) => {
    setGroupToEdit(null);
    try {
      const group: AxiosResponse<AccreditationGroup> = await axios.get(
        `${BASE_URL}/accreditation/groups/${groupIdToEdit}`,
      );
      setGroupToEdit(group.data);
      setCreateGroupOpen(true);
    } catch (error) {}
  };

  const handleDeleteGroupContact = async (individualId: number) => {
    try {
      await axios.delete(
        `${BASE_URL}/accreditation/individuals/${individualId}`,
      );
      invalidateAccreditationGroups(
        queryClient,
        groupId ? parseInt(groupId) : 0,
      );
    } catch (error) {
      console.error("Error deleting group contact:", error);
    }
  };

  // Edit individual
  const editIndividual = async (individualId: number) => {
    try {
      const individual: AxiosResponse<AccreditationIndividual> =
        await axios.get(
          `${BASE_URL}/accreditation/individuals/${individualId}`,
        );
      setIndividual(individual.data);
      setCreateIndividualOpen(true);
    } catch (error) {}
  };

  const closeDeleteGroupDialog = () => {
    setGroupToDeleteDialogOpen(false);
    setTimeout(() => setGroupToDelete(null), 500);
  };

  const closeDeleteIndividualDialog = () => {
    setIndividualToDeleteDialogOpen(false);
    setTimeout(() => setIndividualToDelete(null), 500);
  };

  const closeDeleteGroupContactDialog = () => {
    setGroupContactToDeleteDialogOpen(false);
    setTimeout(() => setGroupContactToModify(null), 500);
  };

  const createGroup = async (request: any) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/accreditation/groups`,
        request,
      );
      console.log("Group created successfully:", response.data);
      // Invalidate and refetch the groups query
      queryClient.invalidateQueries(["groups"]);
    } catch (error) {
      console.error("Error creating group:", error);
    }
  };

  const festivalTemplateGroups = [
    { groupName: "Food Vendors", icon: "🍔" },
    { groupName: "Traders", icon: "💵" },
    { groupName: "Art Department", icon: "🖌️" },
    { groupName: "Artists & Performers", icon: "🎶" },
    { groupName: "Bars", icon: "🍻" },
    { groupName: "Security", icon: "🔐" },
    { groupName: "Production", icon: "👷" },
    { groupName: "Ticketing", icon: "🎟️" },
    { groupName: "Waste Management", icon: "♻️" },
    { groupName: "Medical", icon: "🏥" },
    { groupName: "Volunteers", icon: "🧑‍🏭" },
    { groupName: "Press & Media", icon: "📸" },
  ];

  const clubNightGroups = [
    { groupName: "DJs & Performers", icon: "🎧" },
    { groupName: "Guest List", icon: "🤩" },
    { groupName: "Bar Staff", icon: "🍹" },
    { groupName: "Security", icon: "🔐" },
    { groupName: "Promoters", icon: "📣" },
    { groupName: "Photographers & Videographers", icon: "📸" },
  ];

  const templates = [
    {
      id: "festival",
      name: "Festival",
      subtitle: "Large collection of groups at outdoor music festival",
      href: "#",
      template: (
        <div className="ml-5 mt-5 border dark:border-white/5 rounded-md">
          {festivalTemplateGroups.map((trader, index) => (
            <div
              className={`flex p-2 dark:border-white/10 items-center ${
                index === 0 ? "" : "border-t"
              }`}
            >
              <div className="mr-2">{trader.icon}</div>
              <div className="dark:text-white text-sm font-semibold">
                {trader.groupName}
              </div>
            </div>
          ))}
        </div>
      ),
      onClick: async () => {
        await createGroup(
          festivalTemplateGroups.map((group) => {
            return {
              name: group.groupName,
              icon: group.icon,
              parent: groupId ? groupId : null,
            };
          }),
        );
        setCreateGroupFromTemplateOpen(false);
      },
    },
    {
      id: "club-night",
      name: "Club Night",
      subtitle: "Small collection of groups for a club night",
      href: "#",
      template: (
        <div className="ml-5 mt-5 border dark:border-white/10 rounded-md">
          {clubNightGroups.map((trader, index) => (
            <div
              className={`flex p-2 dark:border-white/10 items-center ${
                index === 0 ? "" : "border-t"
              }`}
            >
              <div className="mr-2">{trader.icon}</div>
              <div className="dark:text-white text-sm font-semibold">
                {trader.groupName}
              </div>
            </div>
          ))}
        </div>
      ),
      onClick: async () => {
        await createGroup(
          clubNightGroups.map((group) => {
            return {
              name: group.groupName,
              icon: group.icon,
              parent: groupId ? groupId : null,
            };
          }),
        );
        setCreateGroupFromTemplateOpen(false);
      },
    },
  ];

  const updateOrder = async (
    model: string,
    payload: Array<{ itemId: number; order: number }>,
  ) => {
    try {
      const transformedPayload = payload.map(({ itemId, order }) => ({
        itemId,
        order,
      }));

      await axios.post(
        `${BASE_URL}/accreditation/${model}/order`,
        transformedPayload,
      );
    } catch (error) {
      console.error("Error updating order", error);
    }
  };

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery(
    ["groups", groupId, groupSearch, groupPage],
    () =>
      fetchGroups(groupPage, {
        parent: groupId ? groupId : "null",
        search: groupSearch,
      }),
  );

  const { data: individualsData, isLoading: isLoadingIndividuals } = useQuery(
    ["individuals", groupId, individualSearch, individualPage],
    () =>
      fetchIndividuals(individualPage, {
        group: groupId ? groupId : "null",
        search: individualSearch,
      }),
    {
      enabled: !!groupId,
    },
  );

  const isEmpty =
    !groupsData?.results.length && !individualsData?.results.length;

  const { data: flowsData, isLoading: flowsLoading } = useQuery(
    ["flows", "manual", "individual"],
    async () => {
      const { data } = await axios.get(
        `${BASE_URL}/flows?trigger_type=manual&target_type=individual`,
      );
      return data;
    },
  );

  const bulkActionIndividuals = useMutation(
    async (payload: { action: string; ids: number[] }) => {
      setBulkActionsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/accreditation/individuals/bulk-action`,
        payload,
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Bulk action on individuals completed successfully");
        queryClient.invalidateQueries(["individuals", groupId?.toString()]);
      },
      onError: (error) => {
        console.error("Error performing bulk action on individuals:", error);
        toast.error("Failed to perform bulk action on individuals");
      },
      onSettled: () => {
        setBulkActionsLoading(false);
        queryClient.invalidateQueries({ queryKey: ["individuals"] });
      },
    },
  );

  const bulkActionGroups = useMutation(
    async (payload: { action: string; ids: number[] }) => {
      setBulkActionsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/accreditation/groups/bulk-action`,
        payload,
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Bulk action on groups completed successfully");
        queryClient.invalidateQueries(["groups"]);
      },
      onError: (error) => {
        console.error("Error performing bulk action on groups:", error);
        toast.error("Failed to perform bulk action on groups");
      },
      onSettled: () => {
        setBulkActionsLoading(false);
      },
    },
  );

  // For triggering flows for bulk actions
  const triggerFlow = useMutation(
    async ({ flow, ids }: { flow: Flow; ids: any }) => {
      const response = await axios.post(
        `${BASE_URL}/flows/${flow.id}/start`,
        ids,
      );
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        toast.success(`${variables.flow.name} triggered successfully`);
        console.log(
          `Flow "${variables.flow}" triggered successfully for IDs:`,
          variables.ids,
        );
      },
      onError: (error, variables) => {
        console.error(
          `Error triggering ${variables.flow.name} flow`,
          variables.ids,
          error,
        );
        toast.error(`Failed to trigger flow "${variables.flow.id}"`);
      },
    },
  );

  // Bulk actions
  // for Groups
  const GROUP_BULK_ACTIONS: Array<BulkAction> = [
    {
      name: "Send group contact invitation",
      icon: SendIcon,
      handler: (selectedIds: Set<number>) => {
        bulkActionGroups.mutate({
          action: "Send group contact invitation",
          ids: Array.from(selectedIds),
        });
      },
    },
    {
      name: "Send tickets",
      icon: TicketsIcon,
      handler: (selectedIds: Set<number>) => {
        bulkActionGroups.mutate({
          action: "Send tickets",
          ids: Array.from(selectedIds),
        });
      },
    },
    {
      name: "Delete",
      icon: TrashIcon,
      isDestructive: true,
      handler: (selectedIds: Set<number>) => {
        bulkActionGroups.mutate({
          action: "Delete",
          ids: Array.from(selectedIds),
        });
      },
    },
    {
      name: "Move",
      icon: CornerUpRight,
      handler: (selectedIds: Set<number>) => {},
      component: MoveIndividualOrGroup,
    },
  ];

  // for Individuals
  const INDIVIDUAL_BULK_ACTIONS: Array<BulkAction> = [
    {
      name: "Send tickets",
      icon: TicketsIcon,
      handler: (selectedIds: Set<number>) => {
        console.log("Sending tickets for IDs:", Array.from(selectedIds));
        bulkActionIndividuals.mutate({
          action: "Send tickets",
          ids: Array.from(selectedIds),
        });
      },
    },
    {
      name: "Delete",
      icon: TrashIcon,
      isDestructive: true,
      handler: (selectedIds: Set<number>) => {
        bulkActionIndividuals.mutate({
          action: "Delete",
          ids: Array.from(selectedIds),
        });
      },
    },
    {
      name: "Compose email",
      icon: EnvelopeIcon,
      isDestructive: false,
      handler: (selectedIds: Set<number>) => {
        // todo, send email
      },
    },
    {
      name: "Move",
      icon: CornerUpRight,
      handler: (selectedIds: Set<number>) => {},
      component: MoveIndividualOrGroup,
    },
  ];

  // if we have flows, we can add them as bulk actions
  if (flowsData?.results) {
    INDIVIDUAL_BULK_ACTIONS.push({
      name: "Flows",
      icon: BoltIcon,
      handler: (selectedIds: Set<number>) => {},
      subActions: flowsData.results.map((flow) => ({
        name: flow.name,
        icon: SendIcon,
        handler: (selectedIds: Set<number>) => {
          console.log(
            `Triggering flow "${flow.name}" for selected IDs:`,
            Array.from(selectedIds),
          );
          triggerFlow.mutate({
            flow: flow,
            ids: { individualIds: Array.from(selectedIds) },
          });
        },
      })),
    });
  }

  const reformatIndividuals = (individuals: AccreditationIndividual[]) => {
    return individuals.map((individual) => ({
      ...individual,
      tickets: individual.tickets.map((ticket) => ticket.allocation.ticket.id),
    }));
  };

  const individualContainerRef = useRef<HTMLDivElement>(null);
  const [sheetWidth, setSheetWidth] = useState<number>(1000);

  // Update sheetWidth on window resize, but don't shrink below 1000px
  useEffect(() => {
    const updateWidth = () => {
      if (individualContainerRef.current) {
        const containerWidth = individualContainerRef.current.clientWidth;
        setSheetWidth(containerWidth < 1000 ? 1000 : containerWidth + 47);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [individualContainerRef.current?.clientWidth]);

  const updateGroupMutation = useMutation(
    (group: ManageAccreditationGroupRequest) => {
      return axios.put(
        `${BASE_URL}/accreditation/groups/${data?.uuid}/manage`,
        group,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["group", groupId]);
        queryClient.invalidateQueries(["individuals", groupId]);
        invalidateAccreditationGroups(queryClient, data?.id || 0);
        toast.success("Sheet saved successfully");
      },
      onError: (error) => {
        console.error("Error updating group:", error);
        toast.error("Failed to update group");
        setIsSavingSheet(false);
        if (error?.response?.data) {
          for (const message of error.response.data) {
            toast.error(message);
          }
        }
      },
      onSettled: () => {
        setIsSavingSheet(false);
      },
    },
  );

  const [sheetData, setSheetData] = useState<string[][]>();
  const [sheetSelectedRows, setSheetSelectedRows] = useState<Set<number>>(
    new Set(),
  );

  const [isSavingSheet, setIsSavingSheet] = useState(false);

  const saveSheet = () => {
    setIsSavingSheet(true);
    const individuals = sheetData?.map((row) => {
      // Parse tickets JSON string (row[4]) into array of {id, value}
      let tickets: { id: number; value: string }[] = [];
      try {
        const ticketObj = row[4] ? JSON.parse(row[4]) : {};
        // For each id/qty, push {id, value: "dummy"} qty times
        Object.entries(ticketObj).forEach(([id, qty]) => {
          for (let i = 0; i < Number(qty); i++) {
            tickets.push({ id: Number(id), value: "dummy" });
          }
        });
        console.log("Parsed tickets:", tickets);
      } catch {
        tickets = [];
      }
      return {
        firstName: row[0],
        lastName: row[1],
        email: row[2],
        isGroupContact: row[3],
        tickets,
        group: groupId ? parseInt(groupId) : null,
        uuid: row[5] ? row[5] : uuidv4(), // <-- Use the last element for uuid
      };
    });
    // create yup validation schema to validate the above and, run the validation and throw toast if any
    individualsValidationSchemaWithUuid
      .validate(
        { individuals: individuals },
        { abortEarly: false, context: { individuals } }, // <-- add context here
      )
      .then((validIndividuals) => {
        console.log("Validated individuals:", validIndividuals);
        // proceed to send to backend
        updateGroupMutation.mutate({
          individuals: validIndividuals.individuals,
        });
      })
      .catch((validationError) => {
        console.log("Validation errors:", validationError);
        if (validationError.inner && validationError.inner.length > 0) {
          validationError.inner.forEach((err: any) => {
            toast.error(err.message);
          });
        }
      });
  };

  return (
    <DashboardLayout requiredPermissions={["view_user"]}>
      {/* <AccreditationHorizontalNav activePage="Groups" /> */}

      {isLoadingGroups || isLoadingIndividuals ? (
        <div className="w-full h-screen flex items-center justify-center -mt-44">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Loading colour={isDarkMode ? "white" : "black"} />
            </div>

            <h1 className="dark:text-zinc-400 font-medium text-zinc-600">
              Loading groups, won't be a moment!
            </h1>
          </div>
        </div>
      ) : (
        <>
          {/* Global Search */}
          <GlobalSearch />

          {/* Create group from Template Dialog */}
          <Transition.Root show={createGroupFromTemplateOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              // initialFocus={cancelButtonRef}
              onClose={setCreateGroupFromTemplateOpen}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 dark:bg-zinc-900 dark:bg-opacity-90 bg-opacity-75 backdrop-blur-md transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg dark:bg-dark-primary bg-white px-10 py-10 pt-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                      <div className="">
                        <Dialog.Title
                          as="h3"
                          className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                        >
                          Create Groups from Template
                        </Dialog.Title>
                        <div>
                          <div className="mx-auto">
                            <div className="mt-8 grid grid-cols-2 gap-y-12 xl:gap-x-8">
                              {templates.map((template) => (
                                <div key={template.id}>
                                  <div className="relative">
                                    <div className="relative h-72 w-full overflow-hidden rounded-lg border dark:border-white/5">
                                      {template.template}
                                    </div>
                                    <div className="relative mt-4">
                                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                        {template.name}
                                      </h3>
                                      <p className="mt-1 text-sm text-gray-500">
                                        {template.subtitle}
                                      </p>
                                    </div>
                                    <div className="absolute inset-x-0 top-0 flex h-72 items-end justify-end overflow-hidden rounded-lg p-4">
                                      <div
                                        aria-hidden="true"
                                        className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black opacity-50"
                                      />
                                    </div>
                                  </div>
                                  <div className="mt-6">
                                    <button
                                      type="button"
                                      id={template.id}
                                      onClick={() => template.onClick()}
                                      className="relative flex items-center justify-center rounded-md border border-transparent bg-gray-100 px-8 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200"
                                    >
                                      Create from template
                                      <span className="sr-only">
                                        , {template.name}
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>

          {/* Create/Edit Group Dialog */}
          <CreateGroupDialog groupId={groupId} />

          {/* Create/Edit Individual Dialog */}
          {data && <CreateIndividualDialog group={data} />}

          {data && (
            <>
              <AllocateTickets group={data} /> <GroupSettings group={data} />
              {/* only show it if we have individuals, i.e. we are in the bottom most group */}
              <SendEmail group={data} />
            </>
          )}

          {/* Delete Group Dialog */}
          <Transition.Root show={groupToDeleteDialogOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              // initialFocus={cancelButtonRef}
              onClose={() => closeDeleteGroupDialog()}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 dark:bg-zinc-900 dark:bg-opacity-90 bg-opacity-75 backdrop-blur-md transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg dark:bg-dark-primary bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <ExclamationTriangleIcon
                            className="h-6 w-6 text-red-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <Dialog.Title
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                          >
                            Delete {groupToDelete?.name}
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Are you sure you want to delete? All of your data
                              will be permanently removed forever. This action
                              cannot be undone.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                          onClick={() => {
                            handleDelete(groupToDelete.id);
                            closeDeleteGroupDialog();
                          }}
                        >
                          Yes, delete
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                          onClick={() => closeDeleteGroupDialog()}
                          // ref={cancelButtonRef}
                        >
                          Cancel
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>

          {/* Delete Group Contact Dialog */}
          <Transition.Root show={groupContactToDeleteDialogOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              // initialFocus={cancelButtonRef}
              onClose={() => closeDeleteGroupContactDialog()}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 dark:bg-zinc-900 dark:bg-opacity-90 bg-opacity-75 backdrop-blur-md transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg dark:bg-dark-primary bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <ExclamationTriangleIcon
                            className="h-6 w-6 text-red-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <Dialog.Title
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                          >
                            Delete {groupContactToModify?.firstName}{" "}
                            {groupContactToModify?.lastName}
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Are you sure you want to delete? All of your data
                              will be permanently removed forever. This action
                              cannot be undone.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                          onClick={() => {
                            handleDeleteGroupContact(groupContactToModify.id);
                            closeDeleteGroupContactDialog();
                          }}
                        >
                          Yes, delete
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                          onClick={() => closeDeleteGroupContactDialog()}
                          // ref={cancelButtonRef}
                        >
                          Cancel
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>

          {/* Delete Individual Dialog */}
          <Transition.Root show={individualToDeleteDialogOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              // initialFocus={cancelButtonRef}
              onClose={closeDeleteIndividualDialog}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-gray-500 dark:bg-zinc-900 dark:bg-opacity-90 bg-opacity-75 backdrop-blur-md transition-opacity" />
              </Transition.Child>

              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  >
                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg dark:bg-dark-primary bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <ExclamationTriangleIcon
                            className="h-6 w-6 text-red-600"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <Dialog.Title
                            id="delete-individual-name"
                            as="h3"
                            className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                          >
                            Delete {individualToDelete?.firstName}{" "}
                            {individualToDelete?.lastName}
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Are you sure you want to delete? All data related
                              to this individual will be permanently removed
                              forever. This action cannot be undone.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:ml-10 sm:mt-4 sm:flex sm:pl-4">
                        <button
                          id="delete-individual-button"
                          type="button"
                          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                          onClick={() => {
                            handleIndividualDelete(individualToDelete);
                            closeDeleteIndividualDialog();
                          }}
                        >
                          Yes, delete
                        </button>
                        <button
                          type="button"
                          className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:ml-3 sm:mt-0 sm:w-auto"
                          onClick={() => closeDeleteIndividualDialog()}
                          // ref={cancelButtonRef}
                        >
                          Cancel
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>

          <>
            {data && (
              <div className="pl-3 pr-1 py-5 border-b dark:border-white/5">
                <GroupDetails
                  onGroupUpdate={() =>
                    queryClient.invalidateQueries(["group", groupId])
                  }
                  group={data}
                  openCreateGroupDrawer={() => {
                    setGroupToEdit(null);
                    setCreateGroupOpen(true);
                  }}
                />
              </div>
            )}

            <div className="bg-red-0 flex w-full min-h-screen">
              <div className="w-full">
                {isEmpty && selectedIndividualViewMode === "Table" ? (
                  <div className="w-full pb-36 pt-28">
                    <div className="text-center">
                      <h3 className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">
                        No Groups or Individuals yet
                      </h3>
                      <p className="mt-2 text-sm text-gray-500">
                        An accreditation group is a collection of groups or
                        individuals that are granted access
                      </p>

                      <div className="mt-6 flex justify-center">
                        <div className="w-2/3">
                          <div>
                            <h3 className="font-medium dark:text-zinc-500 text-zinc-800 text-sm">
                              Get started with
                            </h3>
                          </div>
                          <div className="mt-3 flex justify-center flex-wrap">
                            {[
                              {
                                id: "create-group",
                                name: "Create group",
                                onClick: () => {
                                  setCreateGroupOpen(true);
                                  setGroupToEdit(null);
                                },
                                icon: BoxesIcon,
                              },
                              {
                                id: "create-individual",
                                name: "Create individual",
                                onClick: () => {
                                  setCreateIndividualOpen(true);
                                  setIndividual({} as AccreditationIndividual);
                                },
                                icon: UserIcon,
                              },
                              {
                                id: "create-from-template",
                                name: "Create from template",
                                onClick: () =>
                                  setCreateGroupFromTemplateOpen(true),
                                icon: Shapes,
                              },
                              {
                                id: "import",
                                name: "Import (coming soon)",
                                onClick: () => {},
                                icon: DownloadIcon,
                              },
                            ].map((button) => (
                              <button
                                key={button.id}
                                id={button.id}
                                onClick={button.onClick}
                                className="mb-2 mr-2 dark:text-white dark:ring-white/10 flex justify-center rounded-md dark:bg-zinc-800 bg-white px-3 py-2 text-sm font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
                              >
                                <button.icon className="h-5 w-5 text-gray-400 mr-1.5" />
                                {button.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className={clsx(
                        "flex justify-between pl-4 items-center",
                        selectedIndividualViewMode === "Table" &&
                          " border-b dark:border-white/5",
                        !groupId && "border-b py-2 pr-2",
                        groupId && "py-2",
                      )}
                    >
                      <TabNav
                        items={[
                          { name: "Overview", icon: TableOfContents },
                          { name: "Emails", icon: Mail },
                        ]}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                      />
                      <div className="flex justify-center">
                        {/* we can show the group options button only if we are not in a group context. if we are that can be displayed in group details */}
                        {!groupId && (
                          <div className="ml-2">
                            <GroupOptionsButton />
                          </div>
                        )}

                        {selectedTab === "Overview" &&
                          selectedIndividualViewMode === "Sheet" && (
                            <div className="ml-2 mr-3">
                              <button
                                onClick={() => saveSheet()}
                                className="flex items-center dark:text-white dark:ring-white/10 inline-flex justify-center gap-x-1.5 rounded-md dark:bg-zinc-800 bg-white px-3 py-[0.3rem] text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:hover:bg-zinc-800 hover:bg-gray-100"
                              >
                                {isSavingSheet ? (
                                  <div className="flex items-center">
                                    <ButtonSpinner
                                      colour={isDarkMode ? "white" : "black"}
                                    />
                                    <span>Saving...</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <SaveIcon className="w-4 h-4 mr-1.5" />
                                    Save sheet
                                  </div>
                                )}
                              </button>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* <div className="border-t dark:border-white/10">Filters</div> */}
                    <div
                      className="relative w-full flex"
                      ref={individualContainerRef}
                    >
                      {selectedTab === "Overview" && (
                        <>
                          {selectedIndividualViewMode === "Table" && (
                            <GroupList
                              name="Individuals"
                              onRowClick={(id: number) =>
                                navigate(
                                  `/dashboard/accreditation/individuals/${id}`,
                                )
                              }
                              handleDelete={(
                                individual: AccreditationIndividual,
                              ) => {
                                setIndividualToDeleteDialogOpen(true);
                                setIndividualToDelete(individual);
                              }}
                              onOrderUpdate={updateOrder}
                              handleEditOpen={editIndividual}
                              renderRowContent={(
                                individual: AccreditationIndividual,
                              ) => (
                                <div className="flex items-center space-x-2">
                                  <div>
                                    {individual.icon ? (
                                      individual.icon
                                    ) : (
                                      <UserIcon className="h-6 w-6 text-gray-400 mr-1 -ml-1" />
                                    )}
                                  </div>
                                  <div className="dark:text-white text-zinc-800 text-sm font-semibold min-w-48">
                                    <div className="flex">
                                      {individual.firstName}{" "}
                                      {individual.lastName}
                                      <div className="flex ml-2">
                                        {individual.isGroupContact && (
                                          <div className="has-tooltip relative">
                                            <span className="inline-flex items-center rounded-md bg-blue-400/10 px-1.5 py-1 text-xs font-medium text-blue-400 ring-1 ring-inset ring-blue-400/30 mr-1">
                                              <Shield className="h-3 w-3" />
                                            </span>
                                            <div className="tooltip rounded shadow-lg px-2 py-1 -mt-8 -ml-3 top-0 -translate-x-1/4 text-nowrap bg-dark-secondary text-xs text-gray-400 border border-white/10 font-light">
                                              Group contact
                                            </div>
                                          </div>
                                        )}
                                        {individual.invitationSent && (
                                          <div className="has-tooltip relative">
                                            <span className="inline-flex items-center rounded-md bg-yellow-500/10 px-1.5 py-1 text-xs font-medium text-yellow-400 ring-1 ring-inset ring-yellow-500/20 mr-1">
                                              <Send className="h-3 w-3" />
                                            </span>
                                            <div className="tooltip rounded shadow-lg px-2 py-1 -mt-8 -ml-3 top-0 -translate-x-1/4 text-nowrap bg-dark-secondary text-xs text-gray-400 border border-white/10 font-light">
                                              Invitation sent
                                            </div>
                                          </div>
                                        )}
                                        {individual.lastTimeTicketsSent && (
                                          <div className="has-tooltip relative">
                                            <span className="inline-flex items-center rounded-md bg-green-500/10 px-1.5 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-500/20 mr-1.5">
                                              <Ticket className="h-3 w-3" />
                                            </span>
                                            <div className="tooltip rounded shadow-lg px-2 py-1 -mt-8 -ml-10 top-0 -translate-x-1/4 text-nowrap bg-dark-secondary text-xs text-gray-400 border border-white/10 font-light">
                                              Tickets sent at{" "}
                                              {formatDateWithTime(
                                                individual.lastTimeTicketsSent,
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <p className="font-light text-xs text-gray-400 underline decoration-dotted">
                                      {/* <span className="inline-flex items-center rounded-md bg-green-500/10 px-1.5 py-0.5 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-500/20 mr-2">
                                <DollarSignIcon className="h-2.5 w-2.5 mr-1" />
                                Paid for
                              </span> */}
                                      {individual.email}
                                    </p>
                                  </div>
                                  {data && (
                                    <div
                                      className={
                                        individual.flowRuns &&
                                        individual.flowRuns.length > 0
                                          ? "pr-2"
                                          : ""
                                      }
                                    >
                                      {individual.tickets.map((ticket) => (
                                        <div
                                          key={ticket.id}
                                          className="inline-flex"
                                        >
                                          <TicketBadge
                                            ticket={ticket}
                                            groupId={data?.id}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {individual.flowRuns &&
                                    individual.flowRuns.length > 0 && (
                                      <div className="pl-4 border-l dark:border-white/10">
                                        {individual.flowRuns.map((flowRun) => (
                                          <div
                                            className="flex items-center space-x-2"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              navigate(
                                                `/dashboard/flows/${flowRun.flow.id}/runs?flowRun=${flowRun.id}`,
                                              );
                                            }}
                                          >
                                            <h3 className="dark:text-white text-sm font-light">
                                              {flowRun.flow.name}
                                            </h3>
                                            <StatusBadge
                                              status={flowRun.status}
                                              statusOverride={
                                                flowRun.status === "waiting"
                                                  ? "Waiting on completion"
                                                  : undefined
                                              }
                                            />
                                            <SquareArrowOutUpRightIcon className="h-4 w-4 text-gray-400 ml-2" />
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                </div>
                              )}
                              data={individualsData}
                              groupHeading={
                                <h1 className="dark:text-white font-medium text-sm">
                                  Individuals
                                </h1>
                              }
                              onSearch={(search: string) =>
                                setIndividualSearch(search)
                              }
                              hideOptionsButton={!!groupId}
                              page={individualPage}
                              onPageChange={(page: number) => {
                                setIndividualPage(page);
                              }}
                              bulkActions={INDIVIDUAL_BULK_ACTIONS}
                            />
                          )}

                          {selectedIndividualViewMode === "Sheet" &&
                            individualContainerRef.current && (
                              <div className="w-full">
                                {sheetSelectedRows.size > 0 && (
                                  <div className="border-t py-1 px-2 flex justify-between items-center">
                                    <div className="ml-2 text-sm text-gray-500 flex">
                                      {sheetSelectedRows.size} selected
                                    </div>
                                    <div>
                                      <BulkActionsButton
                                        type="individual"
                                        actions={INDIVIDUAL_BULK_ACTIONS}
                                        selectedIds={sheetSelectedRows}
                                      />
                                    </div>
                                  </div>
                                )}
                                <Sheet
                                  columns={[
                                    { name: "First Name", type: "text" },
                                    { name: "Last Name", type: "text" },
                                    { name: "Email", type: "text" },
                                    { name: "Group Contact", type: "checkbox" },
                                    {
                                      name: "Tickets",
                                      type: "select-with-quantities",
                                      options: data.groupAllocatedTickets.map(
                                        (allocation) => ({
                                          id: allocation.ticket.id,
                                          value: allocation.ticket.name,
                                        }),
                                      ),
                                    },
                                    { name: "uuid", type: "read-only" }, // <-- Add this
                                  ]}
                                  initialData={individualsData.results.map(
                                    (individual) => [
                                      individual.firstName,
                                      individual.lastName,
                                      individual.email,
                                      individual.isGroupContact,
                                      JSON.stringify(
                                        individual.tickets.reduce<
                                          Record<number, number>
                                        >((acc, item) => {
                                          // Count the number of times each ticket id appears
                                          const ticketId =
                                            item.allocation.ticket.id;
                                          acc[ticketId] =
                                            (acc[ticketId] || 0) + 1;
                                          return acc;
                                        }, {}),
                                      ),
                                      individual.uuid,
                                    ],
                                  )}
                                  onSelectedRowsChange={(selectedRows) => {
                                    if (sheetData) {
                                      // whatever is in selected Rows is the index selected, use that to get IDs at those indexes in the sheetData
                                      const selectedIndividuals = selectedRows
                                        .map((i) => individualsData.results[i])
                                        .filter(
                                          (id): id is number =>
                                            id !== undefined,
                                        );
                                      setSheetSelectedRows(
                                        new Set(
                                          selectedIndividuals.map(
                                            (ind) => ind.id,
                                          ),
                                        ),
                                      );
                                    }
                                  }}
                                  onTableDataChange={(data) => {
                                    setSheetData(data);
                                  }}
                                  bulkActions={INDIVIDUAL_BULK_ACTIONS}
                                />
                              </div>
                            )}

                          <GroupList
                            name="Groups"
                            onRowClick={(id: number) =>
                              navigate(`/dashboard/accreditation/groups/${id}`)
                            }
                            handleDelete={(group: AccreditationGroup) => {
                              setGroupToDeleteDialogOpen(true);
                              setGroupToDelete(group);
                            }}
                            onOrderUpdate={updateOrder}
                            handleEditOpen={handleEditOpen}
                            renderRowContent={(group: AccreditationGroup) => (
                              <div className="flex items-center space-x-2">
                                <div>
                                  {group.icon ? (
                                    group.icon
                                  ) : (
                                    <BoxesIcon className="h-5 w-5 text-gray-400 mx-1" />
                                  )}
                                </div>
                                <div className="dark:text-white text-zinc-800 text-sm font-semibold">
                                  {group.name}
                                </div>
                                <div className="pl-3">
                                  {/* here we want to show the hierarchialAllocations */}
                                  {group.groupAndChildrenAllocatedTickets.map(
                                    (ticketAllocation: TicketAllocation) => (
                                      <span className="mr-1.5 mb-1 inline-flex items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20">
                                        {ticketAllocation.claimed}/
                                        {ticketAllocation.quantity}{" "}
                                        {ticketAllocation.ticket.name}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                            data={groupsData}
                            groupHeading={
                              <h1 className="dark:text-white font-medium text-sm">
                                Groups
                              </h1>
                            }
                            onSearch={(search: string) =>
                              setGroupSearch(search)
                            }
                            hideOptionsButton={!!groupId}
                            page={groupPage}
                            onPageChange={(page: number) => {
                              setGroupPage(page);
                            }}
                            bulkActions={GROUP_BULK_ACTIONS}
                          />
                        </>
                      )}

                      {selectedTab === "Emails" && data && (
                        <ViewGroupEmails group={data} />
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        </>
      )}
    </DashboardLayout>
  );
};
